"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation"; 
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale } from "@/context/LocaleContext";

import { useList } from "@/hooks/useList";
import { RootListPaket } from "@/types/List";
import { GemIcon } from "@/components/ui/icons/GemIcon";

const formatRupiah = (angka: number) => {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
    }).format(angka).replace("Rp", "Rp.");
};

const BadgeCheckIcon = () => (
    <svg 
        width="18" 
        height="18" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="1.5" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className="text-gray-800 flex-shrink-0 mt-[2px]"
    >
        <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
        <path d="m9 12 2 2 4-4" />
    </svg>
);

export default function Price() {
    const router = useRouter(); 
    const { t, locale } = useLocale(); 

    const { data: pakets, isLoading: isLoadingPackages } = useList<RootListPaket>("/api/list/paket", true);
    const packages = pakets?.data || [];

    const carouselRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const localePrefix = locale === "en" ? "/en" : "";

    const checkScrollPosition = useCallback(() => {
        if (carouselRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
            setCanScrollLeft(scrollLeft > 20);
            setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 20);
        }
    }, []);

    useEffect(() => {
        if (!isLoadingPackages) {
            const timeoutId = setTimeout(checkScrollPosition, 100);
            window.addEventListener('resize', checkScrollPosition);
            return () => {
                clearTimeout(timeoutId);
                window.removeEventListener('resize', checkScrollPosition);
            };
        }
    }, [isLoadingPackages, checkScrollPosition]);

    const scroll = (direction: 'left' | 'right') => {
        if (carouselRef.current) {
            const scrollAmount = 360; 
            carouselRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
            setTimeout(checkScrollPosition, 300);
        }
    };

    const handleSelectPackage = (packageId: number) => {
        // Simpan paket ID terpilih ke sessionStorage agar bisa di-checkout setelah step-three
        sessionStorage.setItem('preSelectedPackage', packageId.toString());
        router.push(`${localePrefix}/add-event`);
    };

    return (
        <section className="w-full bg-gray-50 pt-26 pb-20 md:pt-32 md:pb-24">
            <div className="max-w-7xl mx-auto px-5 md:px-8 mb-16">
                <h1 className="text-[26px] md:text-[32px] font-extrabold text-[#1A1A1A] mb-4 tracking-tight">
                    {t("LandingPage.price_page_heading") || "Harga Undangan Online Di Elynk.Co"}
                </h1>
                <p className="text-[15px] md:text-[16px] text-gray-700 leading-relaxed max-w-[1100px]">
                    {t("LandingPage.price_page_subheading") || "Temukan paket harga undangan digital yang sesuai dengan kebutuhan Anda! Kami menawarkan berbagai pilihan desain menarik dan fitur lengkap..."}
                </p>
            </div>

            <div className="w-full bg-white pt-16 pb-20 md:pt-20 md:pb-24">
              <div className="text-center mb-10 px-4">
                  <h2 className="text-[24px] md:text-[30px] font-extrabold text-[#1A1A1A] mb-2">
                      {t("LandingPage.package_price_title") || "Pilih Paket Sesuai Kebutuhan Acaramu"}
                  </h2>
                  <p className="text-[15px] md:text-[16px] text-gray-700">
                      {t("LandingPage.package_price_desc") || "Mulai dari paket Gratis, upgrade kapan saja sesuai kebutuhan"}
                  </p>
              </div>

              <div className="relative max-w-[1200px] mx-auto px-4 md:px-8 group">
                  <button 
                      onClick={() => scroll('left')}
                      disabled={!canScrollLeft}
                      className={`flex absolute -left-1 md:-left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full border shadow-md items-center justify-center transition-all duration-300
                          ${canScrollLeft 
                          ? "bg-white border-gray-200 text-gray-600 hover:text-[#E85C57] hover:border-[#E85C57] hover:shadow-lg cursor-pointer" 
                          : "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed" 
                          }`}
                      aria-label="Scroll Left"
                  >
                      <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                  </button>

                  <div 
                      ref={carouselRef}
                      onScroll={checkScrollPosition}
                      className="flex gap-5 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-8 pt-4 relative z-10"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} 
                  >
                      {isLoadingPackages ? (
                          [1, 2, 3].map((n) => (
                              <div key={n} className="min-w-[310px] md:min-w-[340px] flex-1 bg-white rounded-[28px] border border-gray-200 border-t-[10px] border-t-gray-200 p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] animate-pulse h-[550px] snap-center">
                                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                                  <div className="h-4 bg-gray-100 rounded w-full mb-6"></div>
                                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-8"></div>
                                  <div className="space-y-4">
                                      {[1,2,3,4,5,6].map(i => <div key={i} className="h-4 bg-gray-100 rounded w-4/5"></div>)}
                                  </div>
                              </div>
                          ))
                      ) : (
                          packages.map((pkg) => (
                              <div 
                                  key={pkg.id}
                                  className="relative min-w-[310px] md:min-w-[340px] flex-1 bg-white rounded-[28px] border border-gray-200 border-t-[10px] border-t-[#E85C57] shadow-[0_8px_30px_rgba(0,0,0,0.04)] flex flex-col snap-center transition-transform hover:-translate-y-1 duration-300"
                              >
                                  <div className="px-7 pb-8 pt-7 flex flex-col flex-1 relative z-20">
                                      <div className="flex items-center gap-2.5 mb-2 mt-1">
                                          <GemIcon packageName={pkg.package_name} className="w-[24px] h-[24px] flex-shrink-0" />
                                          <h3 className="text-[22px] font-extrabold text-gray-900">
                                              {pkg.package_name}
                                          </h3>
                                      </div>
                                      
                                      <p className="text-[13px] md:text-[14px] text-gray-800 min-h-[42px] leading-snug mb-4 line-clamp-2">
                                          {pkg.desc}
                                      </p>

                                      <div className="mb-6">
                                          {pkg.price_strikethrough ? (
                                              <div className="text-[14px] font-medium text-gray-400 line-through mb-1">
                                                  {formatRupiah(pkg.price_strikethrough)}
                                              </div>
                                          ) : null}
                                          <div className="text-[32px] font-extrabold text-gray-900 tracking-tight leading-none">
                                              {pkg.price === 0 ? "Rp.0" : formatRupiah(pkg.price)}
                                          </div>
                                      </div>

                                      <ul className="space-y-3.5 mb-8 flex-1">
                                          {pkg.fitur?.map((item, index: number) => (
                                              <li key={index} className="flex items-start gap-2.5">
                                                  <BadgeCheckIcon />
                                                  <span className="text-[13.5px] text-gray-800 leading-snug font-medium">
                                                      {item.fitur_detail?.nama_fitur}
                                                  </span>
                                              </li>
                                          ))}
                                      </ul>

                                      <button 
                                          type="button"
                                          onClick={() => handleSelectPackage(pkg.id)}
                                          className="w-full h-[48px] bg-[#E85C57] hover:bg-[#E85C57] text-white font-semibold text-[15px] rounded-xl transition-colors shadow-sm mt-auto"
                                      >
                                          {t("LandingPage.select_package_btn") || "Pilih Paket"}
                                      </button>
                                  </div>
                              </div>
                          ))
                      )}
                  </div>

                  <button 
                      onClick={() => scroll('right')}
                      disabled={!canScrollRight}
                      className={`flex absolute -right-1 md:-right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full border shadow-md items-center justify-center transition-all duration-300
                          ${canScrollRight 
                          ? "bg-white border-gray-200 text-gray-600 hover:text-[#E85C57] hover:border-[#E85C57] hover:shadow-lg cursor-pointer" 
                          : "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed" 
                          }`}
                      aria-label="Scroll Right"
                  >
                      <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                  </button>
              </div>
            </div>
        </section>
    );
}